/**
 * PreviewHeader - Unified header component for document preview pages
 * 
 * Used by both Resume Review (Step 7) and Cover Letter (Step 8) to ensure
 * consistent styling, spacing, and behavior. Any changes here apply to both.
 * 
 * Props:
 * - title: Main heading text
 * - subtitle: Secondary description text
 * - zoomLevel: Current zoom level ('fit', 1, 1.25)
 * - onZoomChange: Callback when zoom changes
 * - showZoom: Whether to show zoom controls (default true)
 * - actions: React node for action buttons (PDF, DOCX, Print)
 * - actionsDisabled: Whether all actions should be disabled
 */

import { Button } from '@/components/ui/button';

// Zoom levels for paper preview - shared across all document previews
export const ZOOM_LEVELS = [
  { label: 'Fit', value: 'fit' },
  { label: '100%', value: 1 },
  { label: '125%', value: 1.25 }
];

export function PreviewHeader({ 
  title,
  subtitle,
  zoomLevel = 'fit',
  onZoomChange,
  showZoom = true,
  actions,
  styleControl,
  actionsDisabled = false
}) {
  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 px-4 lg:px-8 py-4 bg-white border-b border-slate-100 print:hidden" data-testid="preview-header">
      {/* Left: Title + Subtitle */}
      <div>
        <h2 
          className="text-xl font-semibold text-slate-900 tracking-tight" 
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          {title}
        </h2>
        <p className="text-sm text-slate-500">{subtitle}</p>
      </div>
      
      {/* Right: Style + Zoom Controls + Actions */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Presentation Style segmented control */}
        {styleControl}

        {/* Zoom Controls (Desktop only) */}
        {showZoom && onZoomChange && (
          <div className="hidden md:flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            {ZOOM_LEVELS.map((level) => (
              <button
                key={level.label}
                onClick={() => onZoomChange(level.value)}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  zoomLevel === level.value
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid={`zoom-${level.label.toLowerCase().replace('%', '')}`}
              >
                {level.label}
              </button>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        {actions && (
          <div className="flex flex-wrap gap-2">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

export default PreviewHeader;
